(() => {
  if (customElements.get('exv-hero')) return;
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (el, name, fn) => el.addEventListener(name, fn, {signal:this.abort.signal});
      this.slides = [...this.querySelectorAll('.exv-slide')];
      this.tabs = [...this.querySelectorAll('.exv-tab')];
      if (!this.slides.length) return;
      this.index = 0; this.duration = Number(this.dataset.interval || 15) * 1000;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 1023px)');
      this.manualPause = this.dataset.autoplay !== 'true' || this.reduced.matches;
      this.videoOverrides = new WeakMap();
      this.finishedVideos = new WeakSet();
      this.playPending = new WeakSet();
      this.stage = this.querySelector('.exv-stage');
      this.focused = false; this.inView = true; this.editorPause = false;
      this.style.setProperty('--duration', `${this.duration}ms`);
      this.querySelectorAll('[data-prev]').forEach(b => on(b,'click',()=>this.go(this.index-1)));
      this.querySelectorAll('[data-next]').forEach(b => on(b,'click',()=>this.go(this.index+1)));
      this.tabs.forEach((b,i)=>on(b,'click',()=>this.go(i)));
      this.pauseButton = this.querySelector('.exv-pause');
      this.slides.forEach(s=>{
        const button=s.querySelector('.exv-video-toggle');
        if(button) on(button,'click',()=>{
          const video=this.device(s)?.querySelector('video');
          if(!video)return;
          if(video.paused&&(video.ended||this.finishedVideos.has(video))){
            this.finishedVideos.delete(video);
            video.currentTime=0;
          }
          this.videoOverrides.set(video,video.paused);
          this.sync();
        });
        s.querySelectorAll('video').forEach(v=>{
          on(v,'playing',()=>this.videoLabel(s,v));
          on(v,'pause',()=>this.videoLabel(s,v));
          on(v,'ended',()=>{
            // Non-looping videos stay stopped until explicitly played again.
            if(!v.loop){this.finishedVideos.add(v);this.videoOverrides.set(v,false);}
            this.videoLabel(s,v);
          });
          on(v,'error',()=>{if(button){button.hidden=false;button.textContent='Video yüklenemedi — tekrar dene';button.setAttribute('aria-label',button.textContent);}});
        });
      });
      if (this.pauseButton) on(this.pauseButton,'click',()=>{this.manualPause=!this.manualPause;this.sync();});
      on(this,'keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();this.go(this.index+(e.key==='ArrowRight'?1:-1));}});
      on(this,'focusin',()=>{this.focused=true;this.sync();});
      on(this,'focusout',()=>queueMicrotask(()=>{if(this.isConnected){this.focused=this.contains(document.activeElement);this.sync();}}));
      on(document,'visibilitychange',()=>this.sync());
      on(this.mobile,'change',()=>this.sync());
      on(this.reduced,'change',()=>{if(this.reduced.matches)this.manualPause=true;this.sync();});
      let start;
      const finishDrag=()=>{start=null;this.dragging=false;this.classList.remove('is-dragging');this.sync();};
      on(this,'dragstart',e=>{if(!e.target.closest('a,button,input,select,textarea'))e.preventDefault();});
      on(this,'pointerdown',e=>{
        if(this.slides.length<2||e.isPrimary===false||e.button!==0||e.target.closest('a,button,input,select,textarea,.exv-navigation'))return;
        start={x:e.clientX,y:e.clientY,id:e.pointerId};this.dragging=true;this.sync();
        this.setPointerCapture(e.pointerId);
      });
      on(this,'pointermove',e=>{
        if(!start||start.id!==e.pointerId)return;
        const dx=e.clientX-start.x,dy=e.clientY-start.y;
        if(Math.abs(dy)>12&&Math.abs(dy)>Math.abs(dx)){finishDrag();return;}
        if(Math.abs(dx)>10)this.classList.add('is-dragging');
      });
      on(this,'pointerup',e=>{
        if(!start||start.id!==e.pointerId)return;
        const dx=e.clientX-start.x,dy=e.clientY-start.y;
        finishDrag();
        if(Math.abs(dx)>40&&Math.abs(dx)>Math.abs(dy))this.go(this.index+(dx<0?1:-1));
      });
      on(this,'pointercancel',()=>{if(start)finishDrag();});
      on(this,'lostpointercapture',()=>{if(start)finishDrag();});
      on(document,'shopify:block:select',e=>{const i=this.slides.findIndex(s=>s.dataset.blockId===e.detail.blockId);if(i>=0){this.editorPause=true;this.go(i);}});
      on(document,'shopify:block:deselect',()=>{this.editorPause=false;this.sync();});
      this.observer=new IntersectionObserver(entries=>{this.inView=entries[0].isIntersecting;this.sync();},{threshold:.1});
      this.observer.observe(this); this.go(0);
    }
    go(target) {
      clearTimeout(this.timer); this.timer=null;
      const old=this.index, next=(target+this.slides.length)%this.slides.length;
      // Reset offscreen slides without animation before each transition.
      // Otherwise the previously departing slide can enter from the wrong side.
      this.classList.add('exv-preparing');
      this.dataset.direction=target<old?'prev':'next';
      // Flush the starting position before applying the transition classes.
      this.slides.forEach((s,i)=>{if(i!==old)s.classList.remove('is-active','is-leaving');});
      void this.offsetWidth;
      this.classList.remove('exv-preparing');
      void this.offsetWidth;
      this.index=next;
      this.slides.forEach((s,i)=>{
        s.classList.toggle('is-active',i===next);
        s.classList.toggle('is-leaving',i===old&&old!==next);
        s.inert=i!==next;s.setAttribute('aria-hidden',String(i!==next));
      });
      this.tabs.forEach((b,i)=>{
        b.setAttribute('aria-current',String(i===next));
        const line=b.querySelector('i');if(line){line.style.animation='none';void line.offsetWidth;line.style.animation='';}
      });
      this.remaining=this.duration;this.sync();
    }
    sync() {
      if(!this.slides?.length)return;
      // Carousel/editor pause must not freeze the active product video.
      const mediaPaused=document.hidden||!this.inView;
      const paused=this.manualPause||this.focused||this.editorPause||this.dragging||mediaPaused||this.slides.length<2;
      this.dataset.paused=String(paused);
      if(this.pauseButton){this.pauseButton.textContent=this.manualPause?'▶':'Ⅱ';this.pauseButton.setAttribute('aria-label',this.manualPause?'Otomatik geçişi başlat':'Otomatik geçişi durdur');this.pauseButton.setAttribute('aria-pressed',String(this.manualPause));}
      if(paused&&this.timer){this.remaining=Math.max(0,this.remaining-(performance.now()-this.started));clearTimeout(this.timer);this.timer=null;}
      if(!paused&&!this.timer){this.started=performance.now();this.timer=setTimeout(()=>{this.timer=null;this.go(this.index+1);},this.remaining);}
      const currentDevice=this.device(this.slides[this.index]);
      if(this.stage){
        this.stage.dataset.adaptive=String(currentDevice?.dataset.fit==='adaptive');
        this.stage.style.setProperty('--media-ratio',currentDevice?.dataset.ratio||'1.777778');
      }
      this.slides.forEach((s,i)=>{
        const device=this.device(s),button=s.querySelector('.exv-video-toggle');
        if(button)button.hidden=i!==this.index||!device?.querySelector('video');
        s.querySelectorAll('video').forEach(v=>{
          // The slide setting is authoritative, even with an older media snippet.
          if(s.dataset.videoLoop!==undefined){
            v.loop=s.dataset.videoLoop==='true';
            if(!v.loop)v.removeAttribute('loop');
          }
          if(!v.loop&&v.ended)this.finishedVideos.add(v);
          const visible=i===this.index&&device?.contains(v)&&!mediaPaused;
          const wanted=this.videoOverrides.has(v)?this.videoOverrides.get(v):(this.dataset.videoAutoplay!=='false'&&!this.reduced.matches);
          const finished=!v.loop&&this.finishedVideos.has(v);
          if(visible&&wanted&&!finished){
            this.loadVideo(v);
            v.muted=true;v.playsInline=true;
            if(v.paused&&!this.playPending.has(v)){
              this.playPending.add(v);
              v.play().then(()=>{
                // A play promise may settle after navigation or a breakpoint change.
                if(!this.isConnected||s!==this.slides[this.index]||!this.device(s)?.contains(v)||document.hidden||!this.inView||this.videoOverrides.get(v)===false)v.pause();
              }).catch(()=>this.videoLabel(s,v)).finally(()=>this.playPending.delete(v));
            }
          }else if(!v.paused)v.pause();
          if(visible)this.videoLabel(s,v);
        });
      });
    }
    device(slide){return slide?.querySelector(`[data-device="${this.mobile.matches?'mobile':'desktop'}"]`);}
    loadVideo(video){
      let changed=false;
      video.querySelectorAll('source[data-src]').forEach(source=>{source.src=source.dataset.src;source.removeAttribute('data-src');changed=true;});
      if(changed||video.error)video.load();
    }
    videoLabel(slide,video){
      if(!this.device(slide)?.contains(video))return;
      const button=slide.querySelector('.exv-video-toggle');
      if(button){button.textContent=video.paused?'Videoyu oynat':'Videoyu durdur';button.setAttribute('aria-label',button.textContent);}
    }
    disconnectedCallback(){clearTimeout(this.timer);this.observer?.disconnect();this.abort?.abort();this.abort=null;this.querySelectorAll('video').forEach(v=>v.pause());}
  }
  customElements.define('exv-hero',HeroSlider);
})();
